import { useQuery, useQueryClient } from "@tanstack/react-query";

import type { Address } from "viem";
import type { RedKeel } from "../config/networks";
import { eventosDeVault, soloDe } from "./useEventos";


export type Motivo = "reajuste" | "barrido" | "cobro" | "otro";

export const NOMBRE_MOTIVO: Record<Motivo, string> = {
  reajuste: "Reajuste del rango",
  barrido: "Barrido del capital suelto",
  cobro: "Cobro de comisiones",
  otro: "Otra operación del robot",
};

export type Desglose = {
  motivo: Motivo;
  veces: number;
  total0: bigint;
  total1: bigint;
};

export type GastoGas = {
  filas: Desglose[];
  total0: bigint;
  total1: bigint;
  veces: number;
};

/**
 * En que se ha ido la bolsa de gas.
 *
 * `GasReimbursed` no dice POR QUE se cobro, y sin eso el numero total no explica nada: el dueño ve
 * que la bolsa baja y no sabe si es porque el rango se reajusta mucho o porque el robot esta
 * barriendo restos cada hora.
 *
 * El motivo se deduce de los OTROS eventos de la misma transaccion. `_reimburseGas` se llama desde
 * tres sitios (KeelRebalancer.sol:120, 271 y 331) y cada uno deja su rastro:
 *
 *   con `Rebalanced`     -> reajuste del rango
 *   con `Swept`          -> barrido del capital suelto
 *   con `FeesCollected`  -> cobro de comisiones
 *
 * `openPosition` NO reembolsa: la apertura la paga el dueño de su bolsillo.
 */
export function useGas(vault: Address | null, red: RedKeel | undefined) {
  const qc = useQueryClient();

  return useQuery({
    queryKey: ["gas", red?.clave, vault],
    enabled: !!vault && !!red,
    staleTime: 60_000,
    queryFn: async (): Promise<GastoGas> => {
      // TODOS los eventos del vault de una vez, filtrados aquí. Antes era una consulta por evento
      // desde el bloque 0, que en BNB Chain fallaba entera —no acepta ese rango— y el desglose del
      // gas salía a cero como si el robot no hubiera gastado nunca. Comparte consulta con el
      // historial y con la actividad del robot: se piden una vez para las tres pantallas.
      const logs = await eventosDeVault(qc, red!, vault!);
      const gas = soloDe(logs, "GasReimbursed");
      const reajustes = soloDe(logs, "Rebalanced");
      const barridos = soloDe(logs, "Swept");
      const cobros = soloDe(logs, "FeesCollected");

      const txDe = (ls: { transactionHash: string }[]) => new Set(ls.map((l) => l.transactionHash));
      const txReajuste = txDe(reajustes as never);
      const txBarrido = txDe(barridos as never);
      const txCobro = txDe(cobros as never);

      const acc = new Map<Motivo, Desglose>();
      let total0 = 0n, total1 = 0n;

      for (const l of gas as never as Array<{
        transactionHash: string; args: { amount0: bigint; amount1: bigint };
      }>) {
        // El orden importa: un reajuste tambien puede cobrar comisiones en la misma transaccion,
        // y lo que genero el gas fue el reajuste.
        const motivo: Motivo = txReajuste.has(l.transactionHash) ? "reajuste"
          : txBarrido.has(l.transactionHash) ? "barrido"
          : txCobro.has(l.transactionHash) ? "cobro"
          : "otro";

        const d = acc.get(motivo) ?? { motivo, veces: 0, total0: 0n, total1: 0n };
        d.veces += 1;
        d.total0 += l.args.amount0 ?? 0n;
        d.total1 += l.args.amount1 ?? 0n;
        acc.set(motivo, d);
        total0 += l.args.amount0 ?? 0n;
        total1 += l.args.amount1 ?? 0n;
      }

      const orden: Motivo[] = ["reajuste", "barrido", "cobro", "otro"];
      return {
        filas: orden.map((m) => acc.get(m)).filter((d): d is Desglose => !!d),
        total0, total1, veces: gas.length,
      };
    },
  });
}
